// webapp/src/SignupPage.js
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from './supabase';
import config_names from './config_names';
import { 
  FiMail, 
  FiAlertCircle, 
  FiArrowRight, 
  FiArrowLeft,
  FiCheckCircle
} from 'react-icons/fi';
import { FaGoogle } from 'react-icons/fa';
import './auth-pages.css';

function SignupPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [showEmailSignup, setShowEmailSignup] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const navigate = useNavigate();

  const getErrorMessage = (error) => {
    if (!error) return 'An unexpected error occurred. Please try again.';

    switch (error.message) {
      case 'User already registered':
        return 'This email is already in use. Please use a different email.';
      case 'Password should be at least 6 characters':
        return 'The password is too weak. Please use a stronger password (at least 6 characters).';
      default:
        return error.message || 'An unexpected error occurred. Please try again.';
    }
  };

  const handleGoogleSignup = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin + '/dashboard'
        }
      });
  
      if (error) throw error;
      // The redirect will be handled by Google OAuth
    } catch (error) {
      setError(getErrorMessage(error));
    }
  };

  const handleSkip = () => {
    navigate('dashboard');
  };

  const isBlockedDomain = (email) => {
    const blockedDomains = ['euleina.com', 'fursee.com', 'gardsiir.com'];
    const domain = email.toLowerCase().split('@')[1];
    return blockedDomains.includes(domain);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
  
    try {
      // Check if email domain is blocked
      if (isBlockedDomain(email)) {
        // Show fake success message for blocked domains
        setIsSuccess(true);
        console.log("Blocked signup attempt for domain:", email.split('@')[1]);
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
        return;
      }

      // Simple sign-up without any options
      const { data, error } = await supabase.auth.signUp({
        email,
        password
      });
  
      if (error) throw error;
      
      setIsSuccess(true);
      console.log("Signup successful:", data);
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error) {
      console.error("Signup error:", error);
      setError(error.message || "An error occurred during signup");
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Create Account</h1>
          <p>Get started with {config_names.projectName}</p>
        </div>

        {error && (
          <div className="auth-error">
            <FiAlertCircle />
            <span>{error}</span>
          </div>
        )}

        {isSuccess && (
          <div className="auth-success">
            <FiCheckCircle />
            <span>Account created successfully! Please check your email to confirm your account before signing in.</span>
          </div>
        )}

        {!showEmailSignup && !isSuccess ? (
          <>
            {config_names.allow_skip_login_for_now && (
              <button 
                className="auth-btn secondary-btn" 
                onClick={handleSkip}
              >
                <FiArrowRight /> Skip for now
              </button>
            )}
            
            <button 
              className="auth-btn google-btn" 
              onClick={handleGoogleSignup}
            >
              <FaGoogle /> Sign up with Google
            </button>
            
            <div className="auth-divider">or</div>
            
            <button 
              className="auth-btn primary-btn" 
              onClick={() => setShowEmailSignup(true)}
            >
              <FiMail /> Sign up with Email
            </button>
          </>
        ) : !isSuccess ? (
          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <div className="input-icon-wrapper">
                <input 
                  type="email" 
                  className="form-control" 
                  placeholder="Email" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  required 
                />
              </div>
            </div>
            
            <div className="form-group">
              <div className="input-icon-wrapper">
                <input 
                  type="password" 
                  className="form-control" 
                  placeholder="Password (min. 8 characters)" 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  required 
                />
              </div>
            </div>
            
            <button type="submit" className="auth-btn primary-btn">
              <FiArrowRight /> Create Account
            </button>
            
            <button 
              type="button" 
              className="auth-btn secondary-btn" 
              onClick={() => setShowEmailSignup(false)}
            >
              <FiArrowLeft /> Back
            </button>
          </form>
        ) : null}

        {!isSuccess && (
          <div className="auth-footer">
            <span>Already have an account? </span>
            <Link to="/login">Sign in</Link>
          </div>
        )}

        <div className="auth-legal">
          By continuing, you acknowledge that you accept {config_names.projectName_genitive}{' '}
          <a href={`${config_names.domain}/terms`}>Terms of Use</a> and{' '}
          <a href={`${config_names.domain}/privacy`}>Privacy Policy</a>.
        </div>
      </div>
    </div>
  );
}

export default SignupPage;